import { IDictionary } from '@life-line-lab/components-react';
import { useEffect, useState } from 'react';
import { isErrorResponse } from 'Utils/type-guard';

interface UseRefreshDefaultOneFetchQueryProps<T extends IDictionary> {
  fetchService: (...args: any[]) => Promise<T>;
  disabled?: boolean;
}

interface ResponseErrorType {
  isError: boolean;
  errorObject?: any;
}

export type QueryStatusType = {
  isLoading: boolean;
} & ResponseErrorType;

function useDefaultRefreshOneFetchQuery<T>(
  { fetchService, disabled = false }: UseRefreshDefaultOneFetchQueryProps<T>,
  deps: any[],
): [T | null, () => void, QueryStatusType] {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorState, setErrorState] = useState<ResponseErrorType>({
    isError: false,
    errorObject: null,
  });

  const handleFetchData = async () => {
    try {
      const result = await fetchService(...deps);
      if (isErrorResponse(result)) {
        setErrorState({
          isError: true,
          errorObject: result.error,
        });
        return;
      }
      setData(result);
    } catch (err) {}
    finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => !disabled && setIsLoading(true);

  useEffect(() => {
    if (disabled) return;

    setIsLoading(true);
  }, [JSON.stringify(deps)]);

  useEffect(() => {
    if (disabled) return;
    if (!isLoading) return;

    handleFetchData();
  }, [isLoading]);

  return [
    data,
    handleRefresh,
    {
      isLoading,
      ...errorState,
    },
  ];
}

export default useDefaultRefreshOneFetchQuery;
